// =============================================================================
//  hidrataVideoEdu.js
//  UN VÍDEO YA GUARDADO, DE VUELTA EN EL EDITOR
//
//  Rob, 06-10-2026: «¿qué pasa si cometí un error de ortografía, o realicé mal
//  una pregunta, o se desfasó de tiempo? No tenemos un botón de editar
//  ejercicio... Lo ideal es que me lleve al Edu Video Editor con toda la data
//  con la que lo creamos. Con esto evitamos borrar el ejercicio y volver a
//  crearlo de cero, eso sería una pérdida de tiempo y cometería más errores».
//
//  ── POR QUÉ ESTO VIVE SOLO ──────────────────────────────────────────────────
//  Porque es una traducción, y las traducciones fallan en silencio. Lo que se
//  guarda en la base no tiene la misma forma que lo que maneja el editor:
//
//      en la base            en el editor
//      ────────────────────  ──────────────────────────────────────────────
//      start_time            startTime
//      exercises: [{...}]    exercises: [{ id, type, data, status }]
//      (nada)                id de segmento, id de ejercicio, estado
//
//  Si esta traducción pierde un campo, el editor abre un vídeo que PARECE
//  completo y al guardar escribe encima la versión mutilada. No hay aviso, no
//  hay error, y el trabajo de preparar el vídeo entero se va. Por eso es una
//  función suelta que recibe datos y devuelve datos: se mide entera sin
//  navegador, en `audita_reabrir_video.mjs`.
//
//  ── LA REGLA ────────────────────────────────────────────────────────────────
//  Nada se inventa y nada se tira. Un ejercicio que no se entiende se devuelve
//  igualmente, marcado, en vez de desaparecer: desaparecer es justo lo que no
//  se puede permitir aquí.
// =============================================================================

/** Un identificador propio para el editor, que no choca con los que crea él. */
const idDe = (prefijo, i) => `${prefijo}${i}`;

/**
 * Convierte lo que devuelve `GET /api/admin/edu-editor/{id}` en el estado que
 * maneja EduEditorTab.
 *
 * @returns {{
 *   id, videoId, title, channel, topic, language, level, rawTranscript,
 *   segments: Array, ejerciciosLeidos: number, segmentosLeidos: number
 * }}
 */
export function hidrataVideoEdu(video) {
    const v = video || {};
    const edu = (v.edu_data && typeof v.edu_data === 'object') ? v.edu_data : {};
    const guardados = Array.isArray(edu.segments) ? edu.segments : [];

    let ejerciciosLeidos = 0;
    const segments = guardados.map((seg, i) => {
        const s = (seg && typeof seg === 'object') ? seg : {};

        //  El formato de ahora es `exercises: []`; el antiguo era un `exercise`
        //  suelto. Material de hace meses sigue siendo de Rob: si se ignorara,
        //  reabrir un vídeo viejo lo dejaría sin ejercicios y al guardar se
        //  perderían.
        let crudos = Array.isArray(s.exercises) ? s.exercises : [];
        if (!crudos.length && s.exercise) crudos = [s.exercise];

        const exercises = crudos
            .filter(e => e && typeof e === 'object')
            .map((e, j) => {
                ejerciciosLeidos += 1;
                return {
                    id: idDe(`e${i}_`, j),
                    //  Sin tipo no se puede pintar ni regenerar. Se marca como
                    //  quiz —que es el que admite más formas— y el ejercicio se
                    //  conserva tal cual: el editor lo enseña y Rob decide.
                    type: typeof e.type === 'string' && e.type ? e.type : 'quiz',
                    data: e,
                    status: 'ready',
                };
            });

        const inicio = Number(s.start_time) || 0;
        //  Un final que no existe o que va hacia atrás rompería la línea de
        //  tiempo del editor. Treinta segundos es lo que usa el propio servidor
        //  cuando le falta.
        const finCrudo = Number(s.end_time);
        const fin = Number.isFinite(finCrudo) && finCrudo > inicio ? finCrudo : inicio + 30;

        return {
            //  El id es el ÍNDICE, no el que venga guardado: dos segmentos con
            //  el mismo id harían que borrar uno borrara los dos, y eso ha
            //  pasado con material duplicado.
            id: i,
            //  El rótulo del capítulo, si lo tenía. Vive aparte del texto desde
            //  el 07-10-2026: no lo dice nadie en el vídeo.
            titulo: typeof s.titulo === 'string' && s.titulo.trim() ? s.titulo : undefined,
            lines: [],            // no se guardan; sólo las usa el troceado
            startTime: inicio,
            endTime: fin,
            text: typeof s.text === 'string' ? s.text : '',
            exercises,
        };
    });

    return {
        id: v.id ?? null,
        videoId: v.youtube_id || '',
        title: v.title || '',
        channel: v.channel || '',
        topic: v.topic || 'general',
        language: v.language || 'English',
        level: v.level || 'B1',
        //  Si se importó la transcripción, está aquí. Si no, el paso 1 se abre
        //  vacío — y eso es correcto: lo que NO puede pasar es rellenarlo con
        //  los textos de los segmentos pegados, porque entonces «Crear
        //  segmentos» los rehace sin tiempos y se lleva por delante el trabajo.
        rawTranscript: typeof edu.raw_transcript === 'string' ? edu.raw_transcript : '',
        //  ── EL TEXTO EN PANTALLA, DE VUELTA (14-10-2026) ──────────────
        //  Un `PUT` sustituye el `edu_data` ENTERO por el que se manda, así
        //  que lo que no vuelva en el viaje de vuelta se borra. Sin esto,
        //  abrir un vídeo para arreglar una coma y guardar le borraba al
        //  alumno el texto en pantalla de todo el vídeo —y el editor sólo
        //  carga los fragmentos CON ejercicio, así que no habría con qué
        //  reconstruirlo—.
        captions: Array.isArray(edu.captions) ? edu.captions : [],
        segments,
        segmentosLeidos: segments.length,
        ejerciciosLeidos,
    };
}

/**
 * El camino de vuelta: el estado del editor, listo para `PUT .../{id}`.
 *
 * Devuelve también QUÉ SE QUEDA FUERA, porque un `edu_data` nuevo sustituye al
 * viejo entero: un segmento que no entre aquí deja de existir. Quien llame a
 * esto tiene que enseñar esa lista antes de guardar — perder un segmento sin
 * enterarse es el único fallo grave que puede tener esta pantalla.
 */
/**
 * Cuál de los dos textos en pantalla se guarda: el que más renglón tenga.
 *
 * `derivadas` sale de los fragmentos que hay AHORA en el editor. Al reabrir un
 * vídeo sólo se cargan los que llevan ejercicio —diez de noventa y nueve—, así
 * que guardar eso encima del texto bueno sería borrarle al alumno el 90% del
 * vídeo por haber entrado a arreglar una coma.
 *
 * Nunca encoge. Es una regla fea pero es la correcta: equivocarse de más deja
 * texto de sobra, y equivocarse de menos borra trabajo que no se puede
 * reconstruir desde aquí.
 */
export function eligeElTexto(derivadas, cargadas) {
    const a = Array.isArray(derivadas) ? derivadas : [];
    const b = Array.isArray(cargadas) ? cargadas : [];
    return a.length >= b.length ? a : b;
}

export function preparaParaGuardar(segments) {
    const lista = Array.isArray(segments) ? segments : [];
    const dentro = [];
    const fuera = [];

    lista.forEach((s, i) => {
        const listos = (s?.exercises || []).filter(e => e?.status === 'ready' && e?.data);
        if (!listos.length) {
            fuera.push({ numero: i + 1, texto: (s?.text || '').slice(0, 60) });
            return;
        }
        dentro.push({
            id: s.id,
            start_time: s.startTime,
            end_time: s.endTime,
            //  Sólo si lo hay: un `titulo: undefined` viaja como `null` y
            //  ensucia el `edu_data` de todos los fragmentos sin capítulo.
            ...(s.titulo ? { titulo: s.titulo } : {}),
            text: s.text,
            exercises: listos.map(e => e.data),
        });
    });

    //  ── EL TRANSCRIPT ENTERO, PARA EL `CC` DEL ALUMNO (14-10-2026) ────
    //  Arriba se guardan SÓLO los fragmentos con ejercicio, que es una decisión
    //  vieja y a propósito: lo que no tiene ejercicio no es parte del vídeo
    //  jugable. Pero el texto en pantalla sí necesita el vídeo entero.
    //
    //  Rob, 14-10-2026: «el texto del CC sólo se ve algunas veces y la mayoría
    //  sale «·»». No era el reproductor: de un vídeo de 13 minutos con 10
    //  preguntas sólo se guardaban 10 rengloncitos de texto, así que el resto
    //  del rato no había nada que enseñar.
    //
    //  Va APARTE de los segmentos: son dos cosas con dos reglas distintas, y
    //  meterlo dentro obligaría a guardar fragmentos vacíos.
    const r1 = (n) => Math.round((Number(n) || 0) * 10) / 10;
    //  `Number(null)` es 0 y `Number('')` también. Sin este portero, un fragmento
    //  que todavía no se ha marcado —a medio trabajo hay muchos— entraba como si
    //  empezara en el segundo cero, y el texto salía en pantalla encima del
    //  primero de verdad. Es la misma trampa de `tiemposDeLinea.js`.
    const marcado = (v) => {
        if (v === null || v === undefined || v === '') return null;
        const n = Number(v);
        return Number.isFinite(n) ? n : null;
    };
    const captions = lista
        .map((s) => {
            const texto = (s?.text || '').trim();
            const ini = marcado(s?.startTime);
            const fin = marcado(s?.endTime);
            //  Un fragmento sin texto no tiene nada que enseñar, y uno con el
            //  final antes del principio no se puede situar: pintado caería
            //  encima de otro.
            if (!texto || ini === null || fin === null || fin <= ini) return null;
            return { start: r1(ini), end: r1(fin), text: texto };
        })
        .filter(Boolean)
        .sort((a, b) => a.start - b.start);

    return { segments: dentro, descartados: fuera, captions };
}
